import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { map, filter, reduce, cloneDeep, first } from 'lodash';
import { Link } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import ReactResizeDetector from 'react-resize-detector';
import { Modal, Button } from 'react-bootstrap';
import Fa from 'react-fontawesome';
import SearchField from '../../../../components/search-field';
import ProgressBar from '../../../../components/progress-bar';
// Import Actions
import { 
  requestFetchCloseDateFields,
  requestUpdateCloseDateFields,
} from '../../SetupActions';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';

// Import Selectors
import { getViewableDimensions } from '../../../App/AppReducer';
import DataTypeCellRenderer from '../../components/cells/DataTypeCellRenderer';
import ToggleCellRenderer from '../../components/cells/ToggleFieldRenderer';
import APICellRenderer from '../../components/cells/APICellRenderer';
import CustomHeader from '../../../../components/god-table/header';
import FieldLabelCellRenderer from '../../components/cells/FieldLabelCellRenderer';

class CloseDatePage extends Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      loaded: false,
      allUpdating: false,
      fields: [],
      dirty: false,
      navBarHeight: 0,
      showConfirmModal: false,
    };
    this.onGridReady = this.onGridReady.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.loadCustomFields = this.loadCustomFields.bind(this);
    this.markAsCloseDateField = this.markAsCloseDateField.bind(this);
    this.submit = this.submit.bind(this);
    this.onResizNavBar = this.onResizNavBar.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.showConfirm = this.showConfirm.bind(this);
  }

  componentDidMount() {
    this.loadCustomFields();
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }
  onResizNavBar(width, navBarHeight) {
    this.setState({ navBarHeight });
  }
  handleSearch(query) {
    if (this.gridApi) {
      this.gridApi.setQuickFilter(query);
    }
  }
  closeModal() {
    const { disabled, allUpdating } = this.state;
    if (!disabled && !allUpdating) {
      this.setState({ showConfirmModal: false });
    }
  }
  showConfirm() {
    this.setState({ showConfirmModal: true });
  }

  loadFields(rawFields) {
    return reduce(rawFields || [], (items, field) => {
      if (field.sync_required === true || field.is_close_date_field === true) {
        const f = cloneDeep(field);
        if (f.name === 'CloseDate') {
          f.is_default = true;
        }
        f.original_is_close_date_field = f.is_close_date_field;
        items.push(f);
      }
      return items;
    }, []);
  }

  loadCustomFields() {
    this.setState({
      loading: true, error: null, loaded: false, fields: [],
    });
    const allFieldsPromise = this.props.dispatch(requestFetchCloseDateFields());
    allFieldsPromise.then(
      (results) => {
        const fields = this.loadFields(results.fields);
        this.setState({
          loading: false, error: null, loaded: true, fields,
        });
      },
      (error) => {
        this.setState({
          loading: false, error, loaded: false, fields: [],
        });
      },
    );
    return allFieldsPromise;
  }

  markAsCloseDateField(field) {
    const { fields } = this.state;
    const newFields = fields.map((f) => {
      if (f._id === field._id) {
        return { ...f, is_close_date_field: true };
      }
      return { ...f, is_close_date_field: false };
    });
    const dirtyFields = filter(newFields, f => f.original_is_close_date_field !== f.is_close_date_field);
    const dirty = dirtyFields.length > 0;
    this.setState({ dirty, fields: newFields }, () => {
      if (this.gridApi) {
        this.gridApi.refreshCells({ force: true });
      }
    });
  }

  submit() {
    const { fields, allUpdating } = this.state;
    if (allUpdating) {
      return;
    }
    this.setState({ allUpdating: true });
    const selectedField = first(map(filter(fields, { is_close_date_field: true }), 'name'));
    this.props.dispatch(requestUpdateCloseDateFields({ close_date_field: selectedField }))
      .then(
        (results) => {
          const newFields = this.loadFields(results.fields);
          this.setState({ allUpdating: false, fields: newFields, dirty: false, showConfirmModal: false });
        },
        () => {
          this.setState({ allUpdating: false, showConfirmModal: false });
        },
      );
  }


  renderTable() {
    const { viewableDimensions } = this.props;
    const {
      fields, allUpdating, dirty,
      navBarHeight,
    } = this.state;

    if (fields.length === 0) {
      return (
        <div className="alert alert-danger">
          No custom fields of type <code>currency</code> found.
        </div>
      );
    }

    const columnDefs = [
      {
        headerName: 'Field Name',
        field: 'label',
        suppressMenu: true,
        width: 450,
        suppressMovable: true,
        cellRenderer: FieldLabelCellRenderer,
        pinned: 'left',
      },
      {
        headerName: 'API Name',
        field: 'name',
        suppressMenu: true,
        width: 365,
        cellRenderer: APICellRenderer,
        suppressMovable: true,
      },
      {
        headerName: 'Data Type',
        field: 'type',
        suppressMenu: true,
        width: 120,
        suppressMovable: true,
        cellRenderer: DataTypeCellRenderer,
      },
      {
        headerName: 'Use as Close Date Field',
        field: 'is_close_date_field',
        suppressMenu: true,
        width: 200,
        suppressMovable: true,
        cellRenderer: ToggleCellRenderer,
        cellRendererParams: {
          onToggle: this.markAsCloseDateField,
          disabled: allUpdating,
          isRadio: true,
        },
      },  
    ];

    const viewableHeight = viewableDimensions.height - navBarHeight;
    return (
      <div>
        {fields.length > 0 &&
          <nav className="navbar navbar-default navbar-controls">
            <form className="navbar-form navbar-left">
              <SearchField collapsible={false} onSearch={this.handleSearch} />
            </form>
            {dirty &&
              <div className="navbar-form navbar-right animated flash" style={{ marginRight: 5 }}>
                <button onClick={this.submit} disabled={allUpdating} className="btn btn-primary">
                  Save &amp; Update
                </button>
              </div>
            }
            <ReactResizeDetector handleHeight onResize={this.onResizNavBar} />
          </nav>
        }
        <div className="ag-theme-alpine" style={{ height: viewableHeight, width: '100%' }}>
          <AgGridReact
            reactNext
            enableColResize
            enableSorting
            animateRows
            columnDefs={columnDefs}
            getRowNodeId={data => data._id}
            onGridReady={this.onGridReady}
            rowData={fields}
          />
        </div>
      </div>
    );
  }

  renderConfirmModal() {
    const { showConfirmModal, disabled, allUpdating } = this.state;
    return (
      <Modal show={showConfirmModal} bsSize="sm" onHide={this.closeModal} keyboard={!disabled}>
        <Modal.Body>
          <div className="alert alert-warning">
            <Fa name="exclamation-triangle" /> Update may trigger opportunity close date history logs on TruVoice Automation.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="clearfix">
            <Button className="pull-right" bsStyle="primary" disabled={disabled || allUpdating} onClick={this.submit}>
              {allUpdating && <span>Updating</span>}
              {!allUpdating && <span>Update</span>}
            </Button>
            <Button className="pull-left" bsStyle="link" disabled={disabled || allUpdating} onClick={this.closeModal}>
              Cancel
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }

  render() {
    const { organization } = this.props;
    const isDemo = organization && organization.is_demo;
    
    const orgType = organization && organization.type;
    let opportunityObjectName = 'Opportunity';
    if (orgType === 'hubspot') {
      opportunityObjectName = 'deal';
    }

    const { loading, loaded } = this.state;
    return (
      <div className="container">
        <Helmet title="Manage Opportunity Close Date Integration" />

        {!isDemo &&
          <div className="well well-sm">
            <div>
              <p>
                Select the date field from your <code>opportunity</code> object to use for the Opportunity Close Date in TruVoice Automation.
              </p>
              <p>
                If you cannot find the field(s) you are looking for <Link to={`/setup/sync-settings/${opportunityObjectName}`}>add more custom fields to the sync</Link>.
              </p>
            </div>
          </div>
        }
        {isDemo &&
          <div className="alert alert-warning">
            This is a demo organization, please switch back to the original organization to change the settings.
          </div>
        }
        <div>
          {loading && <ProgressBar />}
          {!loading && loaded && this.renderTable()}
          {!loading && this.renderConfirmModal()}
        </div>
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    viewableDimensions: getViewableDimensions(state),
    organization: getUserCurrentOrganization(state),
  };
}

CloseDatePage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  viewableDimensions: PropTypes.shape({}).isRequired,
  organization: PropTypes.shape({}).isRequired,
};

CloseDatePage.contextTypes = {
  router: PropTypes.object,
};

export default connect(mapStateToProps)(CloseDatePage);
